import { useEffect } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Calendar, Mail, Phone, MapPin, AlertTriangle } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useStore } from '@/contexts/StoreContext';

import { Layout7Root } from './Layout7Root';

const TermsOfUse = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config?.stores?.[0];

  useEffect(() => {
    document.title = `Termos de Uso - ${config?.name || 'Nossa Loja'}`;
  }, [config?.name]);

  const formatDate = () => {
    return new Date().toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatOpeningHours = () => {
    const hours = store?.openingHours?.default;
    if (!hours) return 'Segunda a Sexta, 8h às 18h | Sábado, 8h às 12h';

    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

    // Check if all weekdays have the same hours
    const weekdayHours = weekDays.map(day => hours[day]);
    const allWeekdaysSame = weekdayHours.every(
      (h, i, arr) => h?.from === arr[0]?.from && h?.to === arr[0]?.to
    );

    if (allWeekdaysSame && hours.monday?.from && hours.monday?.to) {
      const weekdayTime = `${hours.monday.from} - ${hours.monday.to}`;
      
      if (hours.saturday?.from && hours.saturday?.to) {
        const saturdayTime = `${hours.saturday.from} - ${hours.saturday.to}`;
        return `Segunda a Sexta, ${weekdayTime} | Sábado, ${saturdayTime}`;
      } else {
        return `Segunda a Sexta, ${weekdayTime}`;
      }
    }

    // Fallback
    return 'Consulte horários disponíveis';
  };

  return (
    <Layout7Root withHeader className="bg-gradient-to-br from-background via-muted/30 to-background">
      <Header config={config} />

      <div className="container mx-auto px-4 pt-8 pb-16">
        {/* Hero Section */}
        <section className="py-12 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="p-3 bg-primary/10 rounded-2xl">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                  Termos de Uso
                </h1>
                <p className="text-xl text-muted-foreground mt-2">
                  Condições para utilização do nosso site
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <Badge variant="outline" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Última atualização: {formatDate()}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documento legal
              </Badge>
            </div>
          </div>
        </section>

        {/* Company Information */}
        <section className="mb-12">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Informações da Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Loja:</h4>
                  <p className="text-muted-foreground">{store?.company || config?.name || 'Nome da Empresa'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">CNPJ:</h4>
                  <p className="text-muted-foreground font-mono">{store?.document || 'XX.XXX.XXX/XXXX-XX'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Endereço:</h4>
                  <p className="text-muted-foreground">{store?.place?.address || 'Endereço da empresa'}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Contato:</h4>
                  <div className="space-y-1">
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {store?.phone?.formatted || '(XX) XXXXX-XXXX'}
                    </p>
                    <p className="text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {config?.email || 'contato@empresa.com'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Terms Content */}
        <section className="max-w-4xl mx-auto space-y-8">
          <Card className="border-border/50">
            <CardContent className="p-8">
              <div className="prose prose-slate max-w-none">
                
                <h2 className="text-2xl font-bold text-foreground mb-4">1. Aceitação dos Termos</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Ao acessar e utilizar o site da {store?.company || config?.name || 'nossa empresa'}, 
                  você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar 
                  com qualquer parte destes termos, não deve utilizar nossos serviços.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">2. Descrição dos Serviços</h2>
                <div className="space-y-4 mb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    Nosso site oferece os seguintes serviços:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Visualização de veículos seminovos e zero quilômetro</li>
                    <li>Solicitação de informações sobre veículos específicos</li>
                    <li>Simulação de financiamento e consórcio</li>
                    <li>Agendamento de test-drives e avaliações</li>
                    <li>Contato direto com nossa equipe comercial</li>
                    <li>Acesso a informações sobre serviços automotivos</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-foreground mb-4">3. Uso Adequado do Site</h2>
                <div className="space-y-4 mb-6">
                  <h3 className="text-lg font-semibold text-foreground">3.1 Você concorda em:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Fornecer informações verdadeiras e precisas</li>
                    <li>Utilizar o site apenas para fins legítimos</li>
                    <li>Respeitar a propriedade intelectual</li>
                    <li>Não interferir no funcionamento do site</li>
                    <li>Não tentar acessar áreas restritas</li>
                  </ul>
                  
                  <h3 className="text-lg font-semibold text-foreground">3.2 É proibido:</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Usar o site para atividades ilegais</li>
                    <li>Transmitir vírus ou códigos maliciosos</li>
                    <li>Coletar informações de outros usuários</li>
                    <li>Fazer engenharia reversa do site</li>
                    <li>Spam ou comunicações não solicitadas</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-foreground mb-4">4. Informações dos Veículos</h2>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-800 mb-2">Importante:</h4>
                      <p className="text-amber-700 text-sm leading-relaxed">
                        As informações sobre veículos são fornecidas de boa fé, mas podem estar sujeitas a alterações. 
                        Preços, disponibilidade e especificações podem variar. Recomendamos sempre confirmar as 
                        informações diretamente conosco antes de tomar decisões de compra.
                      </p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-foreground mb-4">5. Propriedade Intelectual</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Todo o conteúdo do site, incluindo textos, imagens, logotipos, design e software, é propriedade 
                  da {store?.company || config?.name || 'empresa'} ou de seus licenciadores. Você não pode reproduzir, 
                  distribuir ou modificar qualquer conteúdo sem autorização prévia por escrito.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">6. Limitação de Responsabilidade</h2>
                <div className="space-y-4 mb-6">
                  <p className="text-muted-foreground leading-relaxed">
                    Na máxima extensão permitida por lei, nossa empresa não será responsável por:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                    <li>Danos diretos, indiretos ou consequenciais</li>
                    <li>Perda de lucros ou oportunidades de negócio</li>
                    <li>Interrupção de serviços ou falhas técnicas</li>
                    <li>Informações incorretas de terceiros</li>
                    <li>Ações de outros usuários do site</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold text-foreground mb-4">7. Disponibilidade do Serviço</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Embora nos esforcemos para manter o site disponível 24/7, não garantimos disponibilidade contínua. 
                  Podemos suspender o serviço para manutenção, atualizações ou por motivos técnicos sem aviso prévio.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">8. Links para Terceiros</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Nosso site pode conter links para sites de terceiros. Não somos responsáveis pelo conteúdo, 
                  políticas de privacidade ou práticas desses sites. Recomendamos revisar os termos de uso 
                  de qualquer site que você visite através de nossos links.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">9. Modificações dos Termos</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Reservamo-nos o direito de modificar estes termos a qualquer momento. Alterações entrarão 
                  em vigor imediatamente após a publicação no site. É sua responsabilidade revisar periodicamente 
                  estes termos para estar ciente de eventuais mudanças.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">10. Lei Aplicável e Foro</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Estes termos são regidos pelas leis brasileiras. Qualquer disputa será resolvida nos 
                  tribunais competentes da comarca onde está situada nossa empresa, renunciando expressamente 
                  a qualquer outro foro, por mais privilegiado que seja.
                </p>

                <h2 className="text-2xl font-bold text-foreground mb-4">11. Contato</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Para dúvidas sobre estes termos ou nossos serviços, entre em contato:
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-muted-foreground">
                    <strong>E-mail:</strong> {config?.email || 'contato@empresa.com'}
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Telefone:</strong> {store?.phone?.formatted || '(XX) XXXXX-XXXX'}
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Endereço:</strong> {store?.place?.address || 'Endereço da empresa'}
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Horário de funcionamento:</strong> {formatOpeningHours()}
                  </p>
                </div>

              </div>
            </CardContent>
          </Card>
        </section>
      </div>

      <Footer config={config} />
    </Layout7Root>
  );
};

export default TermsOfUse;
